import React from 'react';
import PropTypes from 'prop-types';
import { cn } from 'src/lib/utils';
import { daysSince } from './tabFormatters';

// "Waiting to Schedule" column — Scheduling tab only (senior_level_flow's
// "Ready to Schedule" state), wired in via seniorFlowTabFactory's
// extraColumns. Days elapsed from row.updated_at (when the candidate
// entered this state) to today, per explicit instruction to rely on that
// field specifically.
//
// 5+ days waiting renders in the same danger red the rest of this feature
// uses elsewhere (Opportunities.jsx's "Overdue") — a confirmed threshold
// from the shared mockup (6 days → red, 2 days → plain), not a guess.
const WAITING_RED_THRESHOLD_DAYS = 5;

const WaitingToScheduleCell = ({ row }) => {
  const days = daysSince(row.updated_at);

  if (days == null) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  // "0 days" reads as a glitch (an elapsed-time count that's landed on
  // zero) rather than what it actually means — this candidate only
  // entered the state today — so it gets the same dash treatment as the
  // no-data case above, with a "since today" label spelling out why
  // there's no count yet, per explicit request.
  if (days === 0) {
    return (
      <span className="whitespace-nowrap text-sm text-muted-foreground">
        since today
      </span>
    );
  }

  const isUrgent = days >= WAITING_RED_THRESHOLD_DAYS;
  return (
    <span className="whitespace-nowrap">
      <span
        className={cn(
          'text-sm font-semibold',
          isUrgent ? 'text-ds-danger' : 'text-foreground-strong'
        )}
      >
        {days}
      </span>{' '}
      <span className="text-xs text-muted-foreground">
        {days === 1 ? 'day' : 'days'}
      </span>
    </span>
  );
};

WaitingToScheduleCell.propTypes = {
  row: PropTypes.shape({
    updated_at: PropTypes.string,
  }).isRequired,
};

export const waitingToScheduleColumn = {
  key: 'waiting_to_schedule',
  header: 'Waiting to Schedule',
  className: 'text-sm',
  // Left-aligned per explicit request — every senior_level_flow column
  // reads left-to-right like the Candidate column, for a consistent
  // layout across tabs.
  align: 'left',
  width: '160px',
  render: (_v, row) => <WaitingToScheduleCell row={row} />,
};

export default WaitingToScheduleCell;
